import { useState } from "react";
import {
  useStores,
  useMonthEnd,
  useLockedPeriods,
  useLockPeriod,
  useUnlockPeriod,
} from "../hooks/useApi";
import { apiClient } from "../api/client";

interface Store {
  id: string;
  code: string;
  name: string;
}

// Numeric values arrive as strings (Prisma Decimal serialization).
interface RollupRow {
  storeId: string;
  store_name: string;
  month: string;
  itemId: string;
  code: string | null;
  item_name: string;
  unitPrice: string | null;
  section: "DAILY" | "MONTHLY";
  sortOrder: number;
  opening: string;
  purchases: string;
  sales: string;
  left_cook: string;
  damages: string;
  short: string;
  lend: string;
  borrow: string;
  book_closing: string;
  last_physical_count: string | null;
}

function num(value: string | null): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = parseFloat(value);
  return Number.isNaN(n) ? null : n;
}

function fmt(value: number | null, decimals = 0): string {
  if (value === null) return "–";
  return value.toLocaleString("en-ZA", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: Math.max(decimals, 2),
  });
}

interface ComputedRow extends RollupRow {
  varianceNum: number | null;
  stockLossValue: number | null;
}

function computeRow(row: RollupRow): ComputedRow {
  const lastCount = num(row.last_physical_count);
  const bookClosing = num(row.book_closing) ?? 0;
  const unitPrice = num(row.unitPrice);
  const varianceNum = lastCount === null ? null : lastCount - bookClosing;
  const stockLossValue =
    varianceNum === null || unitPrice === null
      ? null
      : varianceNum * unitPrice;
  return { ...row, varianceNum, stockLossValue };
}

function toCsv(rows: ComputedRow[]): string {
  const header = [
    "code",
    "item",
    "section",
    "opening",
    "purchases",
    "sales",
    "left_cook",
    "damages",
    "short",
    "lend",
    "borrow",
    "book_closing",
    "last_physical_count",
    "variance",
    "unit_price",
    "stock_loss_value",
  ];
  const escape = (v: string | number | null) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = rows.map((r) =>
    [
      r.code,
      r.item_name,
      r.section,
      r.opening,
      r.purchases,
      r.sales,
      r.left_cook,
      r.damages,
      r.short,
      r.lend,
      r.borrow,
      r.book_closing,
      r.last_physical_count,
      r.varianceNum,
      r.unitPrice,
      r.stockLossValue?.toFixed(2) ?? "",
    ]
      .map(escape)
      .join(",")
  );
  return [header.join(","), ...lines].join("\n");
}

export function AdminMonthEnd() {
  const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
  const [storeId, setStoreId] = useState("");
  const [month, setMonth] = useState(currentMonth);

  const { data: stores } = useStores();
  const monthDate = `${month}-01`;
  const { data: rollups, isLoading } = useMonthEnd(storeId, monthDate);
  const { data: lockedPeriods } = useLockedPeriods();
  const lockPeriod = useLockPeriod();
  const unlockPeriod = useUnlockPeriod();
  const [lockError, setLockError] = useState("");

  const isLocked = ((lockedPeriods as { storeId: string; month: string }[]) || []).some(
    (l) => l.storeId === storeId && l.month.slice(0, 10) === monthDate
  );

  const handleToggleLock = async () => {
    setLockError("");
    try {
      if (isLocked) {
        if (!window.confirm(`Unlock ${month}? The store can edit entries again.`)) {
          return;
        }
        await unlockPeriod.mutateAsync({ storeId, month: monthDate });
      } else {
        if (
          !window.confirm(
            `Lock ${month}? The store will no longer be able to edit this month's entries.`
          )
        ) {
          return;
        }
        await lockPeriod.mutateAsync({ storeId, month: monthDate });
      }
    } catch (err: any) {
      setLockError(err.response?.data?.error || "Lock operation failed");
    }
  };

  const rows: ComputedRow[] = ((rollups as RollupRow[]) || []).map(computeRow);
  const dailyRows = rows.filter((r) => r.section === "DAILY");
  const monthlyRows = rows.filter((r) => r.section === "MONTHLY");

  const totalStockLoss = rows.reduce(
    (sum, r) => sum + (r.stockLossValue ?? 0),
    0
  );
  const countedItems = rows.filter((r) => r.varianceNum !== null).length;

  const selectedStore = ((stores as Store[]) || []).find(
    (s) => s.id === storeId
  );

  const handleExport = () => {
    const csv = toCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `month-end_${selectedStore?.code ?? "store"}_${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [excelBusy, setExcelBusy] = useState(false);
  const handleExcelExport = async () => {
    if (!storeId) return;
    setExcelBusy(true);
    setLockError("");
    try {
      await apiClient.downloadMonthExcel(
        storeId,
        monthDate,
        `stock_${selectedStore?.code ?? "store"}_${month}.xlsx`
      );
    } catch {
      setLockError("Excel export failed");
    } finally {
      setExcelBusy(false);
    }
  };

  return (
    <div style={{ padding: "1rem" }}>
      <h1>Month-End</h1>

      <div
        style={{
          display: "flex",
          gap: "1rem",
          flexWrap: "wrap",
          alignItems: "flex-end",
          margin: "1rem 0 1.5rem",
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <span style={{ fontSize: "0.875rem", color: "#666" }}>Store</span>
          <select
            value={storeId}
            onChange={(e) => setStoreId(e.target.value)}
            style={{ padding: "0.5rem", minWidth: "200px" }}
          >
            <option value="">Select a store…</option>
            {((stores as Store[]) || []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} — {s.name}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <span style={{ fontSize: "0.875rem", color: "#666" }}>Month</span>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            style={{ padding: "0.5rem" }}
          />
        </label>

        <button
          onClick={handleExport}
          disabled={rows.length === 0}
          style={{
            padding: "0.5rem 1rem",
            backgroundColor: rows.length === 0 ? "#ccc" : "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: rows.length === 0 ? "not-allowed" : "pointer",
          }}
        >
          Export CSV
        </button>

        <button
          onClick={handleExcelExport}
          disabled={!storeId || excelBusy}
          style={{
            padding: "0.5rem 1rem",
            backgroundColor: !storeId || excelBusy ? "#ccc" : "#1d6f42",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: !storeId || excelBusy ? "not-allowed" : "pointer",
          }}
        >
          {excelBusy ? "Exporting…" : "Export Excel (workbook)"}
        </button>

        {storeId && (
          <button
            onClick={handleToggleLock}
            disabled={lockPeriod.isPending || unlockPeriod.isPending}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: isLocked ? "#fff" : "#c33",
              color: isLocked ? "#c33" : "white",
              border: isLocked ? "1px solid #c33" : "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            {isLocked ? "Unlock period" : "Lock period"}
          </button>
        )}

        {storeId && isLocked && (
          <span
            style={{
              padding: "0.35rem 0.75rem",
              backgroundColor: "#fde8ef",
              color: "#a01048",
              borderRadius: "10px",
              fontSize: "0.8rem",
              fontWeight: 600,
            }}
          >
            🔒 Month signed off — read-only
          </span>
        )}
      </div>

      {lockError && (
        <div
          style={{
            backgroundColor: "#fee",
            color: "#c33",
            padding: "0.75rem",
            borderRadius: "4px",
            marginBottom: "1rem",
          }}
        >
          {lockError}
        </div>
      )}

      {!storeId && <p style={{ color: "#666" }}>Pick a store to see its month-end rollup.</p>}
      {storeId && isLoading && <p>Loading…</p>}
      {storeId && !isLoading && rows.length === 0 && (
        <p style={{ color: "#666" }}>No entries recorded for this store in {month}.</p>
      )}

      {rows.length > 0 && (
        <>
          <div
            style={{
              display: "flex",
              gap: "2rem",
              flexWrap: "wrap",
              marginBottom: "1.5rem",
            }}
          >
            <SummaryCard
              label="Total stock loss value"
              value={`R ${fmt(totalStockLoss, 2)}`}
              tone={totalStockLoss < 0 ? "bad" : "good"}
            />
            <SummaryCard
              label="Items with a physical count"
              value={`${countedItems} / ${rows.length}`}
              tone="neutral"
            />
          </div>

          <RollupTable title="Daily items" rows={dailyRows} />
          <RollupTable title="Monthly items" rows={monthlyRows} />
        </>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "good" | "bad" | "neutral";
}) {
  return (
    <div
      style={{
        border: "1px solid #ddd",
        borderRadius: "8px",
        padding: "1rem 1.5rem",
        minWidth: "200px",
      }}
    >
      <div style={{ fontSize: "0.875rem", color: "#666" }}>{label}</div>
      <div
        style={{
          fontSize: "1.5rem",
          fontWeight: "bold",
          color: tone === "bad" ? "#c33" : tone === "good" ? "#282" : "#333",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function RollupTable({ title, rows }: { title: string; rows: ComputedRow[] }) {
  if (rows.length === 0) return null;

  return (
    <div style={{ marginBottom: "2rem" }}>
      <h2 style={{ fontSize: "1.125rem", marginBottom: "0.75rem" }}>{title}</h2>
      <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: "4px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
          <thead style={{ backgroundColor: "#f8f9fa" }}>
            <tr>
              <th style={thStyle}>Item</th>
              <th style={thNumStyle}>Opening</th>
              <th style={thNumStyle}>Purchases</th>
              <th style={thNumStyle}>Sales</th>
              <th style={thNumStyle}>Left Cook</th>
              <th style={thNumStyle}>Damages</th>
              <th style={thNumStyle}>Short</th>
              <th style={thNumStyle}>Lend</th>
              <th style={thNumStyle}>Borrow</th>
              <th style={thNumStyle}>Book Closing</th>
              <th style={thNumStyle}>Last Count</th>
              <th style={thNumStyle}>Variance</th>
              <th style={thNumStyle}>Loss Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.itemId} style={{ borderBottom: "1px solid #eee" }}>
                <td style={tdStyle}>
                  <span style={{ fontWeight: 600 }}>{r.item_name}</span>
                  {r.code && (
                    <span style={{ color: "#888", marginLeft: "0.5rem", fontSize: "0.8rem" }}>
                      {r.code}
                    </span>
                  )}
                </td>
                <td style={tdNumStyle}>{fmt(num(r.opening))}</td>
                <td style={tdNumStyle}>{fmt(num(r.purchases))}</td>
                <td style={tdNumStyle}>{fmt(num(r.sales))}</td>
                <td style={tdNumStyle}>{fmt(num(r.left_cook))}</td>
                <td style={tdNumStyle}>{fmt(num(r.damages))}</td>
                <td style={tdNumStyle}>{fmt(num(r.short))}</td>
                <td style={tdNumStyle}>{fmt(num(r.lend))}</td>
                <td style={tdNumStyle}>{fmt(num(r.borrow))}</td>
                <td style={tdNumStyle}>{fmt(num(r.book_closing))}</td>
                <td style={tdNumStyle}>{fmt(num(r.last_physical_count))}</td>
                <td
                  style={{
                    ...tdNumStyle,
                    color:
                      r.varianceNum === null
                        ? "#888"
                        : r.varianceNum < 0
                          ? "#c33"
                          : r.varianceNum > 0
                            ? "#282"
                            : "#333",
                    fontWeight: r.varianceNum ? 600 : 400,
                  }}
                >
                  {fmt(r.varianceNum)}
                </td>
                <td
                  style={{
                    ...tdNumStyle,
                    color:
                      r.stockLossValue === null
                        ? "#888"
                        : r.stockLossValue < 0
                          ? "#c33"
                          : "#333",
                  }}
                >
                  {r.stockLossValue === null ? "–" : `R ${fmt(r.stockLossValue, 2)}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const thStyle = {
  padding: "0.6rem 0.75rem",
  textAlign: "left" as const,
  fontWeight: "bold" as const,
  borderBottom: "2px solid #ddd",
};

const thNumStyle = { ...thStyle, textAlign: "right" as const };

const tdStyle = {
  padding: "0.6rem 0.75rem",
  borderBottom: "1px solid #eee",
};

const tdNumStyle = { ...tdStyle, textAlign: "right" as const };
